import {memo, useCallback, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {itemShape} from '../shapes';
import {getLocalDateStr, formatDueDate} from '../utils/date';
import {MAX_TITLE_LENGTH} from '../validation';
import useClickOutside from '../hooks/useClickOutside';
import useMenuPosition from '../hooks/useMenuPosition';

const ChecklistItem = memo(function ChecklistItem({
    checklistId,
    item,
    onToggle,
    onDelete,
    onUpdate,
    onStar,
    onSetDueDate,
    shareEditMode,
    readOnly,
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const dateInputRef = useRef(null);
    const menuRef = useRef(null);
    const btnRef = useRef(null);
    const {menuOpen, dropdownPos, toggleMenu, setMenuOpen} = useMenuPosition(btnRef);
    const closeMenu = useCallback(() => setMenuOpen(false), [setMenuOpen]);
    useClickOutside(menuOpen, closeMenu, menuRef, btnRef);

    const handleToggle = useCallback(() => onToggle(checklistId, item.id), [onToggle, checklistId, item.id]);
    const handleTouchEnd = useCallback(
        e => {
            e.preventDefault();
            onToggle(checklistId, item.id);
        },
        [onToggle, checklistId, item.id],
    );
    const handleStar = useCallback(() => {
        onStar(checklistId, item.id);
        setMenuOpen(false);
    }, [onStar, checklistId, item.id, setMenuOpen]);
    const handleDelete = useCallback(() => {
        onDelete(checklistId, item.id);
        setMenuOpen(false);
    }, [onDelete, checklistId, item.id, setMenuOpen]);

    function startEditing() {
        setDraft(item.title);
        setEditing(true);
    }

    function commit() {
        const title = draft.trim();
        if (title && title !== item.title) onUpdate(checklistId, item.id, title);
        setEditing(false);
    }

    function handleKeyDown(e) {
        e.stopPropagation();
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setEditing(false);
    }

    function handleDateInputClick(e) {
        // The date input overlays the calendar button so taps land on it
        // directly (required for iOS). On desktop, clicking a date input
        // doesn't auto-open the picker, so we call showPicker() — the
        // gesture is trusted because it originates from the input itself.
        try {
            e.target.showPicker();
        } catch (_) {
            /* ignore — iOS opens natively */
        }
    }

    function handleDateChange(e) {
        const value = e.target.value;
        onSetDueDate(checklistId, item.id, value || null);
        setMenuOpen(false);
    }

    const today = getLocalDateStr(new Date());
    const isOverdue = !item.checked && item.dueDate && item.dueDate < today;

    const className = [
        'checklist-item',
        item.starred ? 'checklist-item--starred' : '',
        isOverdue ? 'checklist-item--overdue' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={className}>
            {!shareEditMode && (
                <span className="checklist-item-drag-handle" aria-hidden="true">
                    <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="3" cy="2.5" r="1.2" fill="currentColor" />
                        <circle cx="7" cy="2.5" r="1.2" fill="currentColor" />
                        <circle cx="3" cy="7" r="1.2" fill="currentColor" />
                        <circle cx="7" cy="7" r="1.2" fill="currentColor" />
                        <circle cx="3" cy="11.5" r="1.2" fill="currentColor" />
                        <circle cx="7" cy="11.5" r="1.2" fill="currentColor" />
                    </svg>
                </span>
            )}
            <input
                type="checkbox"
                checked={item.checked}
                onChange={handleToggle}
                onTouchEnd={handleTouchEnd}
                disabled={readOnly && !shareEditMode}
            />
            {!shareEditMode && editing ? (
                <input
                    className="checklist-item-title-input"
                    value={draft}
                    maxLength={MAX_TITLE_LENGTH}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={handleKeyDown}
                    autoFocus
                />
            ) : (
                <span
                    className="checklist-item-label"
                    onClick={shareEditMode ? undefined : startEditing}
                    onKeyDown={shareEditMode ? undefined : e => e.key === 'Enter' && startEditing()}
                    role={shareEditMode ? undefined : 'button'}
                    tabIndex={shareEditMode ? undefined : 0}
                >
                    {item.title}
                </span>
            )}
            {!shareEditMode && (
                <div className="checklist-item-actions">
                    {item.dueDate && (
                        <span
                            className={`checklist-item-due-date${isOverdue ? ' checklist-item-due-date--overdue' : ''}`}
                        >
                            {formatDueDate(item.dueDate)}
                        </span>
                    )}
                    {item.starred && (
                        <span className="checklist-item-star-badge" aria-hidden="true">
                            <svg
                                width="9"
                                height="9"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <polygon
                                    points="6,1 7.5,4.5 11.5,4.5 8.5,7 9.5,11 6,8.5 2.5,11 3.5,7 0.5,4.5 4.5,4.5"
                                    stroke="currentColor"
                                    strokeWidth="1"
                                    strokeLinejoin="round"
                                    fill="currentColor"
                                />
                            </svg>
                        </span>
                    )}
                    <div className={`checklist-item-menu${menuOpen ? ' checklist-item-menu--open' : ''}`}>
                        <button
                            ref={btnRef}
                            className="checklist-item-menu-btn"
                            onClick={toggleMenu}
                            aria-label="Item actions"
                            aria-expanded={menuOpen}
                        >
                            <svg
                                width="3"
                                height="12"
                                viewBox="0 0 3 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <circle cx="1.5" cy="2" r="1.4" fill="currentColor" />
                                <circle cx="1.5" cy="6" r="1.4" fill="currentColor" />
                                <circle cx="1.5" cy="10" r="1.4" fill="currentColor" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
            {!shareEditMode &&
                menuOpen &&
                createPortal(
                    <div
                        ref={menuRef}
                        className="checklist-item-menu-dropdown"
                        role="menu"
                        style={{top: dropdownPos.top, right: dropdownPos.right}}
                    >
                        <div className="checklist-item-calendar">
                            <button
                                className="checklist-item-menu-item"
                                aria-label={item.dueDate ? 'Change due date' : 'Set due date'}
                                role="menuitem"
                            >
                                <svg
                                    width="10"
                                    height="10"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <rect
                                        x="1"
                                        y="2"
                                        width="10"
                                        height="9"
                                        rx="1"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                    />
                                    <line x1="1" y1="5" x2="11" y2="5" stroke="currentColor" strokeWidth="1.3" />
                                    <line
                                        x1="4"
                                        y1="1"
                                        x2="4"
                                        y2="3.5"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                    />
                                    <line
                                        x1="8"
                                        y1="1"
                                        x2="8"
                                        y2="3.5"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                    />
                                </svg>
                                {item.dueDate ? `Due: ${formatDueDate(item.dueDate)}` : 'Set due date'}
                            </button>
                            <input
                                ref={dateInputRef}
                                type="date"
                                className="checklist-item-date-input"
                                value={item.dueDate || ''}
                                onClick={handleDateInputClick}
                                onChange={handleDateChange}
                                aria-label="Due date"
                            />
                        </div>
                        <button
                            className={`checklist-item-menu-item${item.starred ? ' checklist-item-menu-item--active' : ''}`}
                            onClick={handleStar}
                            aria-label={item.starred ? 'Unstar item' : 'Star item'}
                            role="menuitem"
                        >
                            <svg
                                width="10"
                                height="10"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <polygon
                                    points="6,1 7.5,4.5 11.5,4.5 8.5,7 9.5,11 6,8.5 2.5,11 3.5,7 0.5,4.5 4.5,4.5"
                                    stroke="currentColor"
                                    strokeWidth="1"
                                    strokeLinejoin="round"
                                    fill={item.starred ? 'currentColor' : 'none'}
                                />
                            </svg>
                            {item.starred ? 'Unstar' : 'Star'}
                        </button>
                        <div className="checklist-item-menu-divider" />
                        <button
                            className="checklist-item-menu-item checklist-item-menu-item--danger"
                            onClick={handleDelete}
                            aria-label="Delete item"
                            role="menuitem"
                        >
                            <svg
                                width="10"
                                height="10"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <polyline
                                    points="1,3 11,3"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M4,3 L4,2 Q4,1 5,1 L7,1 Q8,1 8,2 L8,3"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M2.5,3 L3,10 Q3,11 4,11 L8,11 Q9,11 9,10 L9.5,3"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            Delete
                        </button>
                    </div>,
                    document.querySelector('.App') || document.body,
                )}
        </div>
    );
});

ChecklistItem.propTypes = {
    checklistId: PropTypes.string.isRequired,
    item: itemShape.isRequired,
    onToggle: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onStar: PropTypes.func.isRequired,
    onSetDueDate: PropTypes.func.isRequired,
    shareEditMode: PropTypes.bool,
    readOnly: PropTypes.bool,
};

export default ChecklistItem;
